<template>
    <div class="fields-restricted-manager">
        <div class="alert alert-info" role="alert">
            Sélectionnez les champs que vous souhaitez restreindre. Cocher une catégorie sélectionnera tous ses
            sous-éléments.
        </div>

        <div class="row">
            <div class="col-md-6" v-for="(category, categoryKey) in categories" :key="categoryKey">
                <div class="card mb-4">
                    <!-- En-tête de la catégorie -->
                    <div class="card-header">
                        <div class="custom-control custom-checkbox">
                            <input type="checkbox" class="custom-control-input"
                                   :id="`category-${categoryKey}`"
                                   v-model="allCheckedCategories[categoryKey]"
                                   @change="toggleCategory(categoryKey)">
                            <label class="custom-control-label" :for="`category-${categoryKey}`">
                                <strong>{{ getCategoryName(categoryKey) }}</strong>
                            </label>
                        </div>
                    </div>

                    <!-- Corps de la carte contenant les sous-catégories -->
                    <div class="card-body">
                        <div v-for="(subcategory, subcategoryKey) in category"
                             :key="`${categoryKey}-${subcategoryKey}`"
                             class="mb-3">
                            <!-- Checkbox de sous-catégorie -->
                            <div class="custom-control custom-checkbox">
                                <input type="checkbox" class="custom-control-input"
                                       :id="`subcategory-${categoryKey}-${subcategoryKey}`"
                                       v-model="allCheckedSubcategories[categoryKey][subcategoryKey]"
                                       @change="toggleSubcategory(categoryKey, subcategoryKey)">
                                <label class="custom-control-label"
                                       :for="`subcategory-${categoryKey}-${subcategoryKey}`">
                                    <strong>{{ subcategoryKey }}</strong>
                                </label>
                            </div>

                            <!-- Champs individuels de la sous-catégorie -->
                            <div class="ml-4 mt-2">
                                <div v-for="field in getFields(categoryKey, subcategoryKey)"
                                     :key="`${categoryKey}-${subcategoryKey}-${field}`"
                                     class="custom-control custom-checkbox">
                                    <input type="checkbox" class="custom-control-input"
                                           :id="`field-${categoryKey}-${subcategoryKey}-${field}`"
                                           v-model="selectedFields[categoryKey][subcategoryKey][field]"
                                           @change="updateSelectionState(categoryKey, subcategoryKey)">
                                    <label class="custom-control-label"
                                           :for="`field-${categoryKey}-${subcategoryKey}-${field}`">
                                        {{ field }}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <input type="hidden" name="fields_restricted" :value="JSON.stringify(computedSelectedFieldsForDatas)">
    </div>
</template>

<script>
export default {
    props: {
        userFieldsRestricted: {
            type: String,
            default: '{}'
        },
        fieldsConfig: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            categories: {},
            selectedFields: {},
            allCheckedCategories: {},
            allCheckedSubcategories: {},
            computedSelectedFieldsForDatas: {}
        }
    },

    mounted() {
        this.initializeData();
    },
    watch: {
        selectedFields: {
            deep: true,
            handler() {
                this.computedSelectedFieldsForDatas = this.getSelectedFieldsForSubmit();
            }
        },
        allCheckedCategories: {
            deep: true,
            handler() {
                this.computedSelectedFieldsForDatas = this.getSelectedFieldsForSubmit();
            }
        },
        allCheckedSubcategories: {
            deep: true,
            handler() {
                this.computedSelectedFieldsForDatas = this.getSelectedFieldsForSubmit();
            }
        }
    },


    methods: {
        initializeData() {
            // Charger et parser la configuration des champs
            this.categories = JSON.parse(this.fieldsConfig);

            // Initialiser les structures de données
            this.initializeSelectionStructures();

            // Charger les restrictions existantes
            this.loadExistingRestrictions();

            // Mettre à jour l'état des cases à cocher
            this.updateAllCategoriesState();

            // Initialiser le champ caché avec les valeurs actuelles
            this.computedSelectedFieldsForDatas = this.getSelectedFieldsForSubmit();
        },

        initializeSelectionStructures() {
            this.selectedFields = {};
            this.allCheckedCategories = {};
            this.allCheckedSubcategories = {};

            // Créer la structure pour chaque catégorie et sous-catégorie
            for (const categoryKey in this.categories) {
                this.selectedFields[categoryKey] = {};
                this.allCheckedSubcategories[categoryKey] = {};
                this.allCheckedCategories[categoryKey] = false;

                for (const subcategoryKey in this.categories[categoryKey]) {
                    this.selectedFields[categoryKey][subcategoryKey] = {};
                    this.allCheckedSubcategories[categoryKey][subcategoryKey] = false;

                    // Initialiser chaque champ à false
                    const fields = this.getFields(categoryKey, subcategoryKey);
                    fields.forEach(field => {
                        this.selectedFields[categoryKey][subcategoryKey][field] = false;
                    });
                }
            }
        },

        loadExistingRestrictions() {
            const userFieldsRestricted = JSON.parse(this.userFieldsRestricted || '{}');

            for (const categoryKey in userFieldsRestricted) {
                if (!this.selectedFields[categoryKey]) continue;
                if (Array.isArray(userFieldsRestricted[categoryKey])) {
                    // Obtenir la liste des champs restreints pour cette catégorie
                    const fieldsToRestrict = userFieldsRestricted[categoryKey];

                    // Parcourir toutes les sous-catégories de cette catégorie
                    for (const subcategoryKey in this.selectedFields[categoryKey]) {
                        // Parcourir tous les champs de cette sous-catégorie
                        const fields = this.getFields(categoryKey, subcategoryKey);

                        let allFieldsChecked = true;

                        fields.forEach(field => {
                            // Vérifier si ce champ est dans la liste des champs restreints
                            if (fieldsToRestrict.includes(field)) {
                                // Si oui, le marquer comme coché
                                this.$set(this.selectedFields[categoryKey][subcategoryKey], field, true);
                            } else {
                                allFieldsChecked = false;
                            }
                        });

                        // Mettre à jour l'état de la sous-catégorie
                        this.$set(this.allCheckedSubcategories[categoryKey], subcategoryKey, allFieldsChecked);
                    }

                    // Mettre à jour l'état de la catégorie
                    this.updateCategoryState(categoryKey);
                }
            }
        },

        getFields(categoryKey, subcategoryKey) {
            return Object.keys(this.categories[categoryKey][subcategoryKey] || {});
        },

        toggleCategory(categoryKey) {
            const isChecked = this.allCheckedCategories[categoryKey];

            // Mettre à jour toutes les sous-catégories et leurs champs
            for (const subcategoryKey in this.categories[categoryKey]) {
                this.$set(this.allCheckedSubcategories[categoryKey], subcategoryKey, isChecked);

                const fields = this.getFields(categoryKey, subcategoryKey);
                fields.forEach(field => {
                    this.$set(this.selectedFields[categoryKey][subcategoryKey], field, isChecked);
                });
            }
            this.$forceUpdate();
            this.computedSelectedFieldsForDatas = this.getSelectedFieldsForSubmit();
        },

        toggleSubcategory(categoryKey, subcategoryKey) {
            const isChecked = this.allCheckedSubcategories[categoryKey][subcategoryKey];

            // Mettre à jour tous les champs de cette sous-catégorie
            const fields = this.getFields(categoryKey, subcategoryKey);
            fields.forEach(field => {
                this.$set(this.selectedFields[categoryKey][subcategoryKey], field, isChecked);
            });

            // Mettre à jour l'état de la catégorie parente
            this.updateCategoryState(categoryKey);
            this.$forceUpdate();
            this.computedSelectedFieldsForDatas = this.getSelectedFieldsForSubmit();
        },

        updateSelectionState(categoryKey, subcategoryKey) {
            this.updateSubcategoryState(categoryKey, subcategoryKey);
            this.updateCategoryState(categoryKey);
            this.computedSelectedFieldsForDatas = this.getSelectedFieldsForSubmit();
        },

        updateSubcategoryState(categoryKey, subcategoryKey) {
            // Vérifier si tous les champs de cette sous-catégorie sont sélectionnés
            const fields = this.getFields(categoryKey, subcategoryKey);
            const allChecked = fields.every(field =>
                this.selectedFields[categoryKey][subcategoryKey][field]
            );

            this.allCheckedSubcategories[categoryKey][subcategoryKey] = allChecked;
        },

        updateCategoryState(categoryKey) {
            // Vérifier si toutes les sous-catégories sont sélectionnées
            const allChecked = Object.keys(this.allCheckedSubcategories[categoryKey]).every(
                subcategoryKey => this.allCheckedSubcategories[categoryKey][subcategoryKey]
            );

            this.allCheckedCategories[categoryKey] = allChecked;
        },

        updateAllCategoriesState() {
            // Mettre à jour l'état de toutes les catégories
            for (const categoryKey in this.categories) {
                this.updateCategoryState(categoryKey);
            }
        },

        getCategoryName(categoryKey) {
            const names = {
                'api': 'API',
                'export': 'Export/Affichage'
            };

            return names[categoryKey] || categoryKey;
        },

        getSelectedFieldsForSubmit() {
            const result = {};

            for (const categoryKey in this.selectedFields) {
                result[categoryKey] = [];

                for (const subcategoryKey in this.selectedFields[categoryKey]) {
                    // Récupérer tous les champs sélectionnés dans cette sous-catégorie
                    for (const field in this.selectedFields[categoryKey][subcategoryKey]) {
                        if (this.selectedFields[categoryKey][subcategoryKey][field]) {
                            // Ajouter le champ individuel à la liste
                            result[categoryKey].push(field);
                        }
                    }
                }

                if (result[categoryKey].length === 0) {
                    delete result[categoryKey];
                }
            }

            return result;
        }
    }
}
</script>

<style scoped>
.fields-restricted-manager {
    margin-bottom: 20px;
}
</style>
